import { supabase } from '../lib/supabase';

/**
 * Fetch memorization progress data with phase groupings
 * @param {string} userId - User ID
 * @param {string} timeDomain - Time domain: '30days', '90days', 'year', 'alltime'
 * @returns {Promise<Array>} Array of progress data points
 */
export const getMemorizationProgress = async (userId, timeDomain = '30days') => {
  try {
    const { data, error } = await supabase?.rpc('get_memorization_progress', {
      p_user_id: userId,
      p_time_domain: timeDomain
    });

    if (error) {
      console.error('Error fetching memorization progress:', error);
      throw error;
    }

    // Transform data for chart display
    return data?.map(point => ({
      date: point?.date_point,
      phase1_2: point?.phase_1_2_count || 0,
      phase3_4: point?.phase_3_4_count || 0,
      phase5_6: point?.phase_5_6_count || 0,
      phase7: point?.phase_7_count || 0,
      total: point?.total_count || 0
    })) || [];
  } catch (error) {
    console.error('Failed to fetch memorization progress:', error);
    return [];
  }
};

/**
 * Format date for chart display based on time domain
 * @param {string} dateStr - Date string
 * @param {string} timeDomain - Time domain
 * @returns {string} Formatted date string
 */
export const formatChartDate = (dateStr, timeDomain) => {
  const date = new Date(dateStr);
  
  switch (timeDomain) {
    case '30days': case'90days':
      // Format as "Jan 15"
      return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case 'year':
      // Format as "Week of Jan 15"
      return `${date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    case 'alltime':
      // Format as "Jan 2024"
      return date?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    default:
      return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

/**
 * Fetch verse distribution by current phase
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of phase distribution data
 */
export const getPhaseDistribution = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('verses')
      ?.select('current_phase')
      ?.eq('user_id', userId);

    if (error) {
      console.error('Error fetching phase distribution:', error);
      throw error;
    }

    // Count verses by phase
    const phaseCounts = {
      0: 0,
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
      6: 0,
      7: 0,
    };

    data?.forEach(verse => {
      const phase = verse?.current_phase;
      // Treat any Phase 8 verses (legacy) as Phase 7
      const normalizedPhase = phase === 8 ? 7 : phase;
      if (normalizedPhase !== null && normalizedPhase !== undefined && phaseCounts?.hasOwnProperty(normalizedPhase)) {
        phaseCounts[normalizedPhase]++;
      }
    });

    // Transform to chart format with colors
    const phaseColors = {
      0: '#9ca3af', // gray
      1: '#ef4444', // red
      2: '#f97316', // orange
      3: '#f59e0b', // amber
      4: '#eab308', // yellow
      5: '#84cc16', // lime
      6: '#22c55e', // green
      7: '#3b82f6', // blue (Final Phase)
    };

    const phaseLabels = {
      0: 'Phase 0 (New)',
      1: 'Phase 1 (Daily)',
      2: 'Phase 2 (3 days)',
      3: 'Phase 3 (Weekly)',
      4: 'Phase 4 (2 weeks)',
      5: 'Phase 5 (Monthly)',
      6: 'Phase 6 (45 days)',
      7: 'Phase 7 (Quarterly)',
    };

    return Object.keys(phaseCounts)?.map(phase => ({
      name: phaseLabels?.[phase],
      value: phaseCounts?.[phase],
      color: phaseColors?.[phase]
    }))?.filter(item => item?.value > 0); // Only return phases with verses
  } catch (error) {
    console.error('Failed to fetch phase distribution:', error);
    return [];
  }
};

/**
 * Get total verse count for statistics (counts individual verses in passages)
 * @param {string} userId - User ID
 * @returns {Promise<number>} Total individual verse count
 */
export const getTotalVerseCount = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('verses')
      ?.select('verse_count')
      ?.eq('user_id', userId);

    if (error) {
      console.error('Error fetching total verse count:', error);
      throw error;
    }

    // Sum up all verse_count values (counts individual verses in passages)
    const totalVerses = data?.reduce((sum, verse) => sum + (verse?.verse_count || 1), 0) || 0;
    return totalVerses;
  } catch (error) {
    console.error('Failed to fetch total verse count:', error);
    return 0;
  }
};

/**
 * Get total passage count (memorization units)
 * @param {string} userId - User ID
 * @returns {Promise<number>} Total passage count
 */
export const getTotalPassageCount = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('verses')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('user_id', userId);

    if (error) {
      console.error('Error fetching total passage count:', error);
      throw error;
    }

    return data || 0;
  } catch (error) {
    console.error('Failed to fetch total passage count:', error);
    return 0;
  }
};

/**
 * Get verse statistics with passage support
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Statistics object with passage and verse counts
 */
export const getVerseStatistics = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('verses')
      ?.select('verse_count, verse_start, verse_end')
      ?.eq('user_id', userId);

    if (error) {
      console.error('Error fetching verse statistics:', error);
      throw error;
    }

    // Calculate statistics
    const totalPassages = data?.length || 0;
    const totalVerses = data?.reduce((sum, verse) => sum + (verse?.verse_count || 1), 0) || 0;
    const singleVersePassages = data?.filter(v => v?.verse_start === v?.verse_end)?.length || 0;
    const multiVersePassages = data?.filter(v => v?.verse_start !== v?.verse_end)?.length || 0;

    return {
      totalPassages,        // Total memorization units
      totalVerses,          // Total individual verses for statistics
      singleVersePassages,  // Number of single-verse passages
      multiVersePassages,   // Number of multi-verse passages
      averageVersesPerPassage: totalPassages > 0 ? (totalVerses / totalPassages)?.toFixed(2) : 0
    };
  } catch (error) {
    console.error('Failed to fetch verse statistics:', error);
    return {
      totalPassages: 0,
      totalVerses: 0,
      singleVersePassages: 0,
      multiVersePassages: 0,
      averageVersesPerPassage: 0
    };
  }
};